import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { usePadelScore } from './usePadelScore';
import { ScoreSettingsModal } from './ScoreSettingsModal';
import { MatchConfig } from './types';
import { soundManager } from '../../utils/soundManager';
import { useTranslation } from 'react-i18next';

// Format time (MM:SS)
const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export interface MatchTimerRef {
    reset: () => void;
}

// Extracted MatchTimer component to prevent whole PadelScoreCounter from re-rendering every second
const MatchTimer = React.forwardRef<MatchTimerRef, {
    isRunning: boolean;
    onToggle: () => void;
}>(({ isRunning, onToggle }, ref) => {
    const [timer, setTimer] = useState(0);

    useImperativeHandle(ref, () => ({
        reset: () => setTimer(0)
    }));

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isRunning) {
            interval = setInterval(() => {
                setTimer(t => t + 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isRunning]);

    return (
        <div
            onClick={onToggle}
            className={`flex flex-col items-center justify-center w-14 h-12 rounded-lg cursor-pointer transition-colors shadow-inner ${isRunning ? 'bg-primary/20 text-primary border border-primary/30' : 'bg-surface-3 text-text-secondary border border-white/5'}`}
        >
            <span className="material-symbols-outlined text-lg">{isRunning ? 'pause' : 'play_arrow'}</span>
            <span className="font-mono font-bold text-[10px] leading-none mt-0.5">{formatTime(timer)}</span>
        </div>
    );
});

export const PadelScoreCounter: React.FC = () => {
    const { t } = useTranslation();
    const [config, setConfigState] = useState<MatchConfig>({
        format: '2_SETS',
        goldenPoint: false,
        vibrationEnabled: true,
        soundEnabled: true
    });
    const { state, setConfig, addPoint, undo, reset } = usePadelScore(config);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const [team1Name, setTeam1Name] = useState(t('matchScore.team1Blue'));
    const [team2Name, setTeam2Name] = useState(t('matchScore.team2Red'));

    // Timer Logic
    const timerRef = useRef<MatchTimerRef>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [matchStarted, setMatchStarted] = useState(false);
    const [wakeLock, setWakeLock] = useState<WakeLockSentinel | null>(null);

    // Sync config
    useEffect(() => {
        setConfig(config);
    }, [config, setConfig]);

    // Wakelock implementation
    const toggleWakeLock = async () => {
        try {
            if (wakeLock) {
                await wakeLock.release();
                setWakeLock(null);
            } else {
                if ('wakeLock' in navigator) {
                    // @ts-ignore
                    const lock = await navigator.wakeLock.request('screen');
                    setWakeLock(lock);
                } else {
                    // alert('Wakelock non supporté sur ce navigateur.');
                }
            }
        } catch (err) {
            console.error(err);
        }
    };



    const handleStartMatch = () => {
        soundManager.unlock(); // Unlock audio context on user interaction
        setMatchStarted(true);
        setIsRunning(true);
        toggleWakeLock(); // Try to auto-enable wakelock on start
        window.scrollTo(0, 0);
    };

    const getDisplayPoint = (point: number | string, isTieBreak: boolean, isSuperTieBreak: boolean) => {
        if (isTieBreak || isSuperTieBreak) return point;
        if (point === 0) return '0';
        if (point === 1) return '15';
        if (point === 2) return '30';
        if (point === 3) return '40';
        if (point === 4) return 'AV';
        return point;
    };

    const handleReset = () => {
        reset();
        timerRef.current?.reset();
        setIsRunning(false);
        setMatchStarted(false); // Back to start screen
        setTeam1Name(t('matchScore.team1Blue'));
        setTeam2Name(t('matchScore.team2Red'));
    };

    // --- Feedback Logic (Haptics & Sound) ---
    // Track previous state to detect changes
    const prevStateRef = useRef(state);

    useEffect(() => {
        const prev = prevStateRef.current;
        const curr = state;

        // Don't trigger on initial load or reset
        if (!matchStarted) {
            prevStateRef.current = curr;
            return;
        }

        // Detect Point Change (Simple tap)
        const t1PointChanged = curr.currentGame.t1 !== prev.currentGame.t1;
        const t2PointChanged = curr.currentGame.t2 !== prev.currentGame.t2;

        // Detect current set score change (Game Won)
        const currentSetIndex = curr.currentSetIndex;
        const prevSetT1 = prev.sets[currentSetIndex]?.t1 || 0;
        const currSetT1 = curr.sets[currentSetIndex]?.t1 || 0;
        const prevSetT2 = prev.sets[currentSetIndex]?.t2 || 0;
        const currSetT2 = curr.sets[currentSetIndex]?.t2 || 0;

        const gameWon = currSetT1 > prevSetT1 || currSetT2 > prevSetT2;

        // Detect Set Won (Set Index changed or Match Finished)
        const setWon = (curr.currentSetIndex > prev.currentSetIndex) || (curr.isFinished && !prev.isFinished);

        if (setWon) {
            // SET WON: Long Vibration (2s) + Fanfare Sound
            if (config.vibrationEnabled) navigator.vibrate(2000);
            if (config.soundEnabled) soundManager.play('set');
        } else if (gameWon) {
            // GAME WON: Double Vibration + Success Sound
            if (config.vibrationEnabled) navigator.vibrate([100, 50, 100]);
            if (config.soundEnabled) soundManager.play('game');
        } else if (t1PointChanged || t2PointChanged) {
            // POINT: Short Strong Vibration + Click Sound
            if (config.vibrationEnabled) navigator.vibrate(50);
            if (config.soundEnabled) soundManager.play('point');
        }

        prevStateRef.current = curr;
    }, [state, config.vibrationEnabled, config.soundEnabled, matchStarted]);


    return (
        // FIXED LAYOUT STRUCTURE
        // h-[calc(100dvh-170px)]: 100dvh (viewport height) - ~80px (Header) - ~80px (Navbar) - ~10px (Margins)
        // flex flex-col: Stacks timer/score header + main click area + actions
        <div className="relative w-full flex flex-col overflow-hidden h-[calc(100dvh-170px)] lg:h-[calc(100dvh-120px)] bg-surface-1 rounded-xl shadow-2xl border border-white/5 my-1 mx-auto max-w-5xl touch-none select-none isolate">

            {/* 1. TOP HEADER: Timer + Set Scores */}
            <div className="h-24 bg-surface-2 flex items-center justify-between px-3 md:px-6 shadow-lg z-30 shrink-0 border-b border-white/5 gap-2">

                {/* Timer Button */}
                <MatchTimer
                    ref={timerRef}
                    isRunning={isRunning}
                    onToggle={() => setIsRunning(!isRunning)}
                />

                {/* CENTRAL SCORE BOARD (Sets) */}
                <div className="flex-1 flex items-center justify-center overflow-hidden">

                    {/* LEGENDE NOMS EQUIPES (Aligned with Set Boxes) */}
                    <div className="flex flex-col items-end justify-center py-1 gap-0.5 mr-2 shrink-0">
                        {/* Spacer to align with "Set X" label */}
                        <span className="text-[8px] mb-0.5 opacity-0">{t('matchScore.setLabel')}</span>

                        {/* Team 1 Name */}
                        <div className="h-[20px] md:h-[28px] flex items-center">
                            <span className="text-[10px] md:text-xs font-black text-text-secondary uppercase tracking-tighter truncate max-w-[60px] md:max-w-[100px] leading-none text-right">{team1Name}</span>
                        </div>

                        {/* Spacer for divider */}
                        <div className="h-[1px]"></div>

                        {/* Team 2 Name */}
                        <div className="h-[20px] md:h-[28px] flex items-center">
                            <span className="text-[10px] md:text-xs font-black text-text-secondary uppercase tracking-tighter truncate max-w-[60px] md:max-w-[100px] leading-none text-right">{team2Name}</span>
                        </div>
                    </div>

                    {/* SET BOXES SCROLL AREA */}
                    <div className="flex items-center gap-1 md:gap-3 overflow-x-auto no-scrollbar py-2 mask-linear-fade">
                        {/* Render each SET as a distinct box */}
                        {state.sets.map((s, i) => {
                            const isCurrentSet = i === state.currentSetIndex;
                            const isFinishedSet = i < state.currentSetIndex;

                            // Opacity handling for past/future sets
                            const opacityClass = isCurrentSet ? 'opacity-100 ring-1 ring-white/20 bg-surface-3' : isFinishedSet ? 'opacity-60 bg-surface-1' : 'opacity-30 bg-surface-1 grayscale';

                            return (
                                <div key={i} className={`flex flex-col items-center justify-center rounded-md px-1.5 py-1 min-w-[32px] md:min-w-[44px] transition-all ${opacityClass}`}>
                                    <span className="text-[8px] text-text-secondary uppercase font-bold mb-0.5 tracking-tighter">{t('matchScore.setLabel')} {i + 1}</span>
                                    <div className="flex flex-col gap-0.5 w-full">
                                        {/* Team 1 Score in Set */}
                                        <div className={`flex items-center justify-center h-[20px] md:h-[28px]`}>
                                            <span className={`font-mono font-bold text-lg md:text-xl leading-none ${s.t1 > s.t2 && isFinishedSet ? 'text-primary' : 'text-white'}`}>{s.t1}</span>
                                        </div>
                                        {/* Divider */}
                                        <div className="h-[1px] w-full bg-white/10"></div>
                                        {/* Team 2 Score in Set */}
                                        <div className={`flex items-center justify-center h-[20px] md:h-[28px]`}>
                                            <span className={`font-mono font-bold text-lg md:text-xl leading-none ${s.t2 > s.t1 && isFinishedSet ? 'text-danger' : 'text-white'}`}>{s.t2}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Settings Button */}
                <button
                    onClick={() => setSettingsOpen(true)}
                    className="w-10 h-10 flex items-center justify-center text-text-secondary hover:text-white rounded-full hover:bg-white/5 transition-colors"
                >
                    <span className="material-symbols-outlined">settings</span>
                </button>
            </div>

            {/* 2. MAIN GAME AREA (Split Screen) */}
            <div className="flex-1 flex flex-col lg:flex-row relative min-h-0">

                {/* Winner Overlay */}
                {state.isFinished && (
                    <div className="absolute inset-0 bg-black/80 z-50 flex flex-col items-center justify-center animate-fadeIn backdrop-blur-sm p-4 text-center">
                        <span className="text-6xl mb-4">🏆</span>
                        <h2 className="text-4xl font-bold text-white mb-2">{t('matchScore.victory')}</h2>
                        <h3 className="text-2xl text-primary font-bold uppercase mb-8 tracking-wider">
                            {state.winner === 't1' ? team1Name : team2Name}
                        </h3>
                        <div className="flex gap-4">
                            <button onClick={undo} className="px-6 py-3 bg-white/10 text-white font-bold rounded-full hover:bg-white/20 transition-all">
                                {t('matchScore.undoFinish')}
                            </button>
                            <button onClick={handleReset} className="px-8 py-3 bg-white text-black font-bold rounded-full shadow-xl hover:scale-105 transition-transform">
                                {t('matchScore.newMatchBtn')}
                            </button>
                        </div>
                    </div>
                )}

                {/* TOP HALF: TEAM 1 (BLUE) */}
                <div
                    onClick={() => { soundManager.unlock(); addPoint('t1'); }}
                    className="flex-1 bg-gradient-to-b from-[#4A90E2] to-[#357ABD] active:brightness-110 transition-all duration-75 flex items-center justify-center relative touch-manipulation cursor-pointer overflow-hidden group"
                >
                    {/* Team Name Label */}
                    <div className="absolute top-4 left-4 z-10 bg-black/20 px-3 py-1 rounded-full backdrop-blur-sm">
                        <span className="text-white font-black uppercase text-sm md:text-base tracking-wider truncate max-w-[150px] block shadow-sm">{team1Name}</span>
                    </div>

                    {/* BIG SCORE */}
                    <span className="text-[140px] md:text-[180px] lg:text-[220px] font-black text-white leading-none select-none drop-shadow-xl font-mono scale-100 group-active:scale-105 transition-transform duration-100">
                        {getDisplayPoint(state.currentGame.t1, state.isTieBreak, state.isSuperTieBreak)}
                    </span>

                    {/* Tie Break Indicator */}
                    {(state.isTieBreak || state.isSuperTieBreak) && (
                        <span className="absolute bottom-2 right-4 text-[10px] bg-white/20 text-white px-2 py-0.5 rounded text-xs font-bold uppercase">{t('matchScore.tieBreak')}</span>
                    )}

                    {/* Server Indicator (Visual Cue) */}
                    {/* Can be added later if currentServer logic is exposed */}
                </div>

                {/* BOTTOM HALF: TEAM 2 (RED) */}
                <div
                    onClick={() => { soundManager.unlock(); addPoint('t2'); }}
                    className="flex-1 bg-gradient-to-t from-[#E53E3E] to-[#C53030] active:brightness-110 transition-all duration-75 flex items-center justify-center relative touch-manipulation cursor-pointer overflow-hidden group border-t-2 border-white/20 lg:border-t-0 lg:border-l-2"
                >
                    {/* Team Name Label */}
                    <div className="absolute top-4 right-4 lg:top-auto lg:bottom-4 lg:left-4 z-10 bg-black/20 px-3 py-1 rounded-full backdrop-blur-sm">
                        <span className="text-white font-black uppercase text-sm md:text-base tracking-wider truncate max-w-[150px] block shadow-sm">{team2Name}</span>
                    </div>

                    {/* BIG SCORE */}
                    <span className="text-[140px] md:text-[180px] lg:text-[220px] font-black text-white leading-none select-none drop-shadow-xl font-mono scale-100 group-active:scale-105 transition-transform duration-100">
                        {getDisplayPoint(state.currentGame.t2, state.isTieBreak, state.isSuperTieBreak)}
                    </span>
                </div>

                {/* CENTER FLOATING ACTIONS */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center justify-center pointer-events-none">
                    {/* Undo Button - Perfectly Centered */}
                    <button
                        onClick={(e) => { e.stopPropagation(); soundManager.unlock(); undo(); }}
                        disabled={state.history.length === 0}
                        className="pointer-events-auto w-16 h-16 rounded-full bg-surface-1 border-4 border-surface-2 text-white flex items-center justify-center shadow-2xl active:scale-95 disabled:opacity-0 disabled:scale-50 transition-all duration-300 hover:bg-surface-3 z-50 text-primary"
                        title={t('matchScore.undoPoint')}
                    >
                        <span className="material-symbols-outlined text-3xl font-bold">undo</span>
                    </button>
                </div>
            </div>

            {/* 3. NEW MATCH OVERLAY (If not started) */}
            {!matchStarted && (
                <div className="absolute inset-0 z-50 bg-background/98 backdrop-blur-xl overflow-y-auto animate-fadeIn">
                    <div className="min-h-full w-full flex flex-col items-center justify-center p-6 pb-12">
                        <h1 className="text-3xl font-black text-white mb-2 uppercase tracking-wide text-center mt-4">{t('matchScore.newMatch')}</h1>

                        <button
                            onClick={() => setSettingsOpen(true)}
                            className="mb-8 px-4 py-2 rounded-full border border-white/10 text-text-secondary flex items-center gap-2 hover:bg-surface-2 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest"
                        >
                            <span className="material-symbols-outlined text-sm">settings</span>
                            {t('matchScore.configRules')}
                        </button>

                        {/* Team Names Inputs */}
                        <div className="w-full max-w-xs space-y-4 mb-8">
                            <div>
                                <label className="text-xs text-text-secondary font-bold uppercase ml-2 mb-1 block">{t('matchScore.team1Blue')}</label>
                                <input
                                    type="text"
                                    value={team1Name}
                                    onChange={(e) => setTeam1Name(e.target.value)}
                                    className="w-full bg-surface-2 border-none rounded-lg p-3 text-white font-bold focus:ring-2 focus:ring-primary outline-none text-center"
                                    placeholder={t('matchScore.team1Name')}
                                />
                            </div>
                            <div>
                                <label className="text-xs text-text-secondary font-bold uppercase ml-2 mb-1 block">{t('matchScore.team2Red')}</label>
                                <input
                                    type="text"
                                    value={team2Name}
                                    onChange={(e) => setTeam2Name(e.target.value)}
                                    className="w-full bg-surface-2 border-none rounded-lg p-3 text-white font-bold focus:ring-2 focus:ring-danger outline-none text-center"
                                    placeholder={t('matchScore.team2Name')}
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleStartMatch}
                            className="group relative shrink-0 px-8 py-4 bg-white text-black text-lg font-black uppercase tracking-wider rounded-full shadow-[0_0_30px_rgba(255,255,255,0.3)] hover:shadow-[0_0_50px_rgba(255,255,255,0.5)] hover:scale-105 transition-all duration-300 active:scale-95"
                        >
                            <span className="flex items-center gap-3">
                                <span className="material-symbols-outlined text-2xl">play_circle</span>
                                {t('matchScore.startMatch')}
                            </span>
                            <div className="absolute inset-0 rounded-full border-2 border-white/50 animate-ping opacity-20"></div>
                        </button>
                    </div>
                </div>
            )}

            <ScoreSettingsModal
                isOpen={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                config={config}
                setConfig={setConfigState}
                onReset={handleReset}
            />
        </div>
    );
};
